import { INotificationSlice, INotificationStore } from '@/types'
import { NotificationItem } from '@/types/notification'
import { convertToGMT } from '@/utils/helper'

const initialState: INotificationStore = {
  items: [],
  unreadCount: 0,
}

export const createNotificationSlice = (set: any): INotificationSlice => ({
  notification: initialState,
  addSocketMessage: (item: NotificationItem) => {
    set((state: any) => ({
      notification: {
        ...state.notification,
        items: [item, ...state.notification.items],
        // Incoming socket items always start unread (see useNotificationStorageHook) —
        // this was previously never incremented, so the sidebar badge stayed at 0.
        unreadCount: state.notification.unreadCount + (item.isRead ? 0 : 1),
      },
    }))
  },
  // REST fallback (`eventList`) hydration — fills in history from before this
  // browser was ever connected to the socket (see useGetNotificationHistory).
  // Only adds events not already known (by eventID) and re-sorts the merged
  // list, since hydration can race with live socket messages arriving first.
  hydrateHistory: (items: NotificationItem[]) => {
    set((state: any) => {
      const existingIDs = new Set(state.notification.items.map((e: NotificationItem) => e.eventID))
      const newOnes = items.filter((item) => !existingIDs.has(item.eventID))
      if (newOnes.length === 0) return state

      const merged = [...state.notification.items, ...newOnes].sort(
        (a: NotificationItem, b: NotificationItem) =>
          new Date(convertToGMT(b.eventDateTime)).getTime() - new Date(convertToGMT(a.eventDateTime)).getTime(),
      )

      return {
        notification: {
          ...state.notification,
          items: merged,
        },
      }
    })
  },
  markAsRead: (eventID: string) => {
    set((state: any) => {
      const target = state.notification.items.find((e: NotificationItem) => e.eventID == eventID)
      if (!target || target.isRead) return state
      const updated = state.notification.items.map((e: NotificationItem) =>
        e.eventID == eventID ? { ...e, isRead: true } : e,
      )

      return {
        notification: {
          ...state.notification,
          items: updated,
          unreadCount: Math.max(0, state.notification.unreadCount - 1),
        },
      }
    })
  },
  markAsReadForTopic: (topicID: string) => {
    set((state: any) => {
      let updated = [...state.notification.items]
      let filtered = updated.filter((e) => e.secondID == topicID && !e.isRead)
      if (filtered.length > 0) {
        updated = updated.map((e) => (e.secondID == topicID ? { ...e, isRead: true } : e))
        return {
          notification: {
            ...state.notification,
            items: updated,
            unreadCount: Math.max(0, state.notification.unreadCount - filtered.length),
          },
        }
      } else {
        return state
      }
    })
  },
  markAllAsRead: () => {
    set((state: any) => ({
      notification: {
        ...state.notification,
        items: state.notification.items.map((e: NotificationItem) => ({ ...e, isRead: true })),
        unreadCount: 0,
      },
    }))
  },
  deleteAllMessages: () => {
    set((state: any) => ({
      notification: {
        ...state.notification,
        items: [],
      },
    }))
  },
  reset: () => set(initialState),
})

export default createNotificationSlice
